// Libraries
import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
// MUI
import AddIcon from '@mui/icons-material/Add'
import { Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
// Types
import {
	handleTimesheetServicesDetails,
	KmsVehicle as PageKmsVehicle,
	ServiceExecution,
	Services,
} from '../../../../../store/slices/timesheet/timesheetPageSlice'
import { KmsVehicle as ApprovalKmsVehicle } from '../../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
// Hooks
import { useIsTimesheetApproved } from '../../../../../utils/hooks'
// Store
import { TimesheetStoreDispatch, TimesheetStoreState } from '../../../../../store/timesheetStore'
import { hasChanges } from '../../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalHasChangesSlice'
import { acceptApprovalServices } from '../../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'
// Components
import KmsVehicleRow from '../../../../KmsVehicleRow'
import CustomButton from '../../../../v2/general/CustomButton/CustomButton'
import CustomTextField from '../../../../general/CustomTextField/CustomTextField'
import PresentationalNotesAndComments from '../../../../PresentationalNotesAndComments'

// Styles
const headerTableCellStyle = {
	fontFamily: 'var(--font-family)',
	fontSize: '0.75rem',
	fontWeight: 600,
	lineHeight: '200%',
	letterSpacing: '0.17px',
	color: 'var(--primary-light)',
}
const bodyTableCellStyle = {
	fontFamily: 'var(--font-family)',
	fontSize: '0.75rem',
	fontWeight: 400,
	lineHeight: '130%',
	border: 'none',
	color: 'var(--primary-dark)',
}

interface ServiceDetailsProps {
	serviceDetail: Services
	instance: number
	kmsVehicles: PageKmsVehicle[]
}

export default function ServiceDetails({
	serviceDetail,
	instance,
	kmsVehicles,
}: Readonly<ServiceDetailsProps>): React.JSX.Element {
	// Global state and hooks
	const dispatch = useDispatch<TimesheetStoreDispatch>()
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const isTimeSheetApproved = useIsTimesheetApproved()
	const timesheetApprovalDetailsAccept = useSelector(
		(state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept,
	)

	// Memoized values
	const serviceNotes = useMemo(() => {
		return serviceDetail.serviceExecution?.comments?.trim() ?? ''
	}, [serviceDetail.serviceExecution])
	const isTowDriversService = useMemo(
		() => serviceDetail.mainSecundaryDriver !== null,
		[serviceDetail.mainSecundaryDriver],
	)
	const isSecondaryDriverService = useMemo(
		() => serviceDetail.service.seconddriver ?? false,
		[serviceDetail.service.seconddriver],
	)
	const kmsEmpty = useMemo(() => {
		return (
			kmsVehicles
				?.filter(kmv => kmv.inEmpty)
				.reduce((total, kmv) => total + ((kmv.kmsEnd ?? 0) - (kmv.kmsIni ?? 0)), 0) ?? 0
		)
	}, [kmsVehicles])
	const kmsNotEmpty = useMemo(() => {
		return (
			kmsVehicles
				?.filter(kmv => !kmv.inEmpty)
				.reduce((total, kmv) => total + ((kmv.kmsEnd ?? 0) - (kmv.kmsIni ?? 0)), 0) ?? 0
		)
	}, [kmsVehicles])
	const specialInstructions = useMemo(() => {
		return (
			serviceDetail.service.comments
				?.trim()
				.split('\r\n')
				.filter(excerpt => !excerpt.startsWith('Justificação'))
				.join(' / ') ?? ''
		)
	}, [serviceDetail.service.comments])

	const capitalizeComment = (comment: string) => {
		const s = comment.trim()
		return s ? s[0].toLocaleUpperCase() + s.slice(1) : ''
	}

	// Helper to check valid KMS rows
	const isValidKms = (k: PageKmsVehicle) => {
		const ini = k.kmsIni ?? 0
		const end = k.kmsEnd ?? 0
		return !(ini === 0 && end === 0)
	}

	// Handlers
	const handleAddKmsVehicle = () => {
		const newKmsId = Math.floor(Math.random() * -1000000)
		const lastKmsEnd = kmsVehicles.length > 0 ? kmsVehicles[kmsVehicles.length - 1].kmsEnd : 0

		if (kmsVehicles.length > 0 && lastKmsEnd === 0) {
			return
		}

		const newKmsVehicle: PageKmsVehicle = {
			kmsIni: lastKmsEnd,
			kmsEnd: 0,
			inEmpty: false,
			kmsId: newKmsId,
		}

		const nextKmsVehicles = [...kmsVehicles, newKmsVehicle]

		if (isTimeSheetApproved) {
			const copyOfServices = [...timesheetApprovalDetailsAccept.services]
			const prevService = copyOfServices[instance]
			copyOfServices[instance] = {
				...prevService,
				kmsVehicles: nextKmsVehicles as ApprovalKmsVehicle[],
			}
			dispatch(acceptApprovalServices(copyOfServices))
		} else {
			const updatedServices: Partial<Services>[] = timesheetPage.services.map((service, i) => {
				if (i === instance) {
					const updatedService: Partial<Services> = {
						service: service.service,
						serviceExecution: {
							...service.serviceExecution,
							kmsVehicles: nextKmsVehicles,
						} as ServiceExecution,
					}
					return updatedService
				}
				return { service: service.service }
			})
			dispatch(handleTimesheetServicesDetails(updatedServices))
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	function handleRemoveKmsVehicle(kmsId: number | undefined) {
		const idToRemove = kmsId ?? 0

		if (isTimeSheetApproved) {
			const copyOfServices = [...timesheetApprovalDetailsAccept.services]
			const prevService = copyOfServices[instance]
			const filteredKms = prevService?.kmsVehicles?.filter(item => item.kmsId !== idToRemove)
			copyOfServices[instance] = {
				...prevService,
				kmsVehicles: (filteredKms || []) as ApprovalKmsVehicle[],
			}
			dispatch(acceptApprovalServices(copyOfServices))
		} else {
			const updatedServices: Partial<Services>[] = timesheetPage.services.map((service, i) => {
				if (i === instance) {
					const filteredKms = service.serviceExecution?.kmsVehicles?.filter(item => item.kmsId !== idToRemove)
					const updatedService: Partial<Services> = {
						service: service.service,
						serviceExecution: {
							...service.serviceExecution,
							kmsVehicles: filteredKms || [],
						} as ServiceExecution,
					}
					return updatedService
				}
				return { service: service.service }
			})
			dispatch(handleTimesheetServicesDetails(updatedServices))
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	// Helper to update state and dispatch
	const updateGlobalState = (updatedLocalKms: PageKmsVehicle[]) => {
		const currentKmsVehicles = updatedLocalKms

		if (isTimeSheetApproved) {
			const copyOfServices = [...timesheetApprovalDetailsAccept.services]
			const prevService = copyOfServices[instance]
			copyOfServices[instance] = {
				...prevService,
				kmsVehicles: currentKmsVehicles as ApprovalKmsVehicle[],
			}
			dispatch(acceptApprovalServices(copyOfServices))
		} else {
			const updatedServices: Partial<Services>[] = timesheetPage.services.map((service, i) => {
				if (i === instance) {
					const updatedService: Partial<Services> = {
						service: service.service,
						serviceExecution: {
							...service.serviceExecution,
							kmsVehicles: currentKmsVehicles as PageKmsVehicle[],
						} as ServiceExecution,
					}
					return updatedService
				}
				return { service: service.service }
			})
			dispatch(handleTimesheetServicesDetails(updatedServices))
		}
		dispatch(hasChanges({ hasChangesFromReviewer: true }))
	}

	function handleKmsIniChange(idx: number, value: number) {
		let nextKmsVehicles = kmsVehicles.map((kmv, i) => (i === idx ? { ...kmv, kmsIni: value } : kmv))
		const updatedKmsVehicle = nextKmsVehicles[idx]

		if (!isValidKms(updatedKmsVehicle)) {
			nextKmsVehicles = nextKmsVehicles.filter((_kmv, i) => i !== idx)
		}

		updateGlobalState(nextKmsVehicles)
	}

	function handleKmsEndChange(idx: number, value: number) {
		let nextKmsVehicles = kmsVehicles.map((kmv, i) => (i === idx ? { ...kmv, kmsEnd: value } : kmv))
		const updatedKmsVehicle = nextKmsVehicles[idx]

		if (!isValidKms(updatedKmsVehicle)) {
			nextKmsVehicles = nextKmsVehicles.filter((_kmv, i) => i !== idx)
		}

		updateGlobalState(nextKmsVehicles)
	}

	function handleInEmptyChange(idx: number, value: boolean) {
		const nextKmsVehicles = kmsVehicles.map((kmv, i) => (i === idx ? { ...kmv, inEmpty: value } : kmv))
		updateGlobalState(nextKmsVehicles)
	}

	return (
		<Paper
			sx={{
				padding: 3,
				alignSelf: 'stretch',
				boxShadow: 'none',
				borderRadius: 0,
			}}
		>
			<Typography
				variant='subtitle2'
				sx={{
					fontFamily: 'var(--font-family)',
					fontWeight: 600,
					fontSize: '0.875rem',
					lineHeight: '157%',
					color: 'var(--primary-dark)',
					paddingBottom: 1,
				}}
			>
				Informações do Serviço
			</Typography>
			<Table size='small'>
				<TableHead>
					<TableRow>
						<TableCell sx={headerTableCellStyle}>Cliente</TableCell>
						<TableCell sx={headerTableCellStyle}>ID Serviço</TableCell>
						<TableCell sx={headerTableCellStyle}>Viatura</TableCell>
						<TableCell sx={headerTableCellStyle}>Passageiros (Escalado)</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow>
						<TableCell sx={bodyTableCellStyle}>{serviceDetail.service.clientName}</TableCell>
						<TableCell sx={bodyTableCellStyle}>{serviceDetail.service.serviceId}</TableCell>
						<TableCell sx={bodyTableCellStyle}>{serviceDetail.service.vehicle.fleetNumber}</TableCell>
						<TableCell sx={bodyTableCellStyle}>{serviceDetail.service.pax}</TableCell>
					</TableRow>
				</TableBody>
			</Table>
			{(serviceDetail?.service?.comments?.trim().length ?? 0) > 0 && (
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell sx={headerTableCellStyle}>Instruções Especiais</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{specialInstructions?.split(' / ').map((comment: string, index: number) => (
							<TableRow key={index}>
								<TableCell sx={bodyTableCellStyle}>{capitalizeComment(comment)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					paddingTop: 2,
					paddingBottom: 1,
					alignSelf: 'stretch',
				}}
			>
				<Typography
					variant='subtitle2'
					sx={{
						fontFamily: 'var(--font-family)',
						fontWeight: 600,
						fontSize: '0.875rem',
						lineHeight: '157%',
						color: 'var(--primary-dark)',
					}}
				>
					Registo de Kms Efetuados
				</Typography>
				<CustomButton
					size='small'
					variant='text'
					sx={{ marginLeft: 'auto' }}
					endIcon={
						<AddIcon
							fontSize='small'
							color='inherit'
						/>
					}
					onClick={() => handleAddKmsVehicle()}
				>
					Adicionar Registo
				</CustomButton>
			</Box>

			<Table size='small'>
				<TableHead>
					<TableRow>
						<TableCell sx={headerTableCellStyle}>Registo</TableCell>
						<TableCell sx={headerTableCellStyle}>Kms Início</TableCell>
						<TableCell sx={headerTableCellStyle}>Kms Fim</TableCell>
						<TableCell
							colSpan={2}
							sx={headerTableCellStyle}
						>
							Vazio
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{kmsVehicles?.map((kmv, idx) => (
						<KmsVehicleRow
							kmv={kmv}
							key={kmv.kmsId ?? idx}
							idx={idx}
							kmsVehicles={kmsVehicles}
							isSecondaryDriverService={isTowDriversService && isSecondaryDriverService}
							removeKmsVehicleHandler={handleRemoveKmsVehicle}
							onKmsIniChange={handleKmsIniChange}
							onKmsEndChange={handleKmsEndChange}
							onInEmptyChange={handleInEmptyChange}
						/>
					))}
				</TableBody>
			</Table>
			<Table sx={{ marginTop: '1rem' }}>
				<TableRow>
					<TableCell
						colSpan={5}
						sx={{
							border: 'none',
						}}
					>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 2,
							}}
						>
							<Typography
								sx={{
									fontFamily: 'var(--font-family)',
									fontSize: '0.75rem',
									fontWeight: 600,
									lineHeight: '130%',
									color: 'var(--primary-dark)',
								}}
							>
								Totais de Kms
							</Typography>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 2,
								}}
							>
								<CustomTextField
									aria-label='Kms registry when transporting passengers'
									disabled
									label='Kms Carga'
									size='small'
									type='number'
									value={kmsNotEmpty}
								/>
								<CustomTextField
									disabled
									label='Kms Vazio'
									aria-label='Kms registry when not transporting passengers'
									size='small'
									type='number'
									value={kmsEmpty}
								/>
								<CustomTextField
									disabled
									label='Total Kms'
									aria-label='Total Kms'
									size='small'
									type='number'
									value={kmsEmpty + kmsNotEmpty}
									error={kmsEmpty + kmsNotEmpty < 0}
								/>
							</Box>
						</Box>
					</TableCell>
				</TableRow>
			</Table>
			{/* Notas Adicionais */}
			<PresentationalNotesAndComments
				type='element'
				title='Notas Adicionais'
				label='Notas Adicionais'
				content={serviceNotes}
			/>
		</Paper>
	)
}
